import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import React from 'react';

import { AudioSettings } from './AudioSettings';
import { CompositionSettings } from './CompositionSettings';
import { ImageSettings } from './ImageSettings';
import { ShapeSettings } from './ShapeSettings';
import { TextSettings } from './TextSettings';
import { VideoSettings } from './VideoSettings';

import type { ElementState } from '../../renderer/ElementState';
import type { CompositionTemplate } from './CompositionSettings/types';
import type { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

export type GetActiveElementSettingsProps = {
  activeElement: ElementState;
  videoCreator: ReturnType<typeof useVideoCreatorStore>;
  sidebarRef: React.RefObject<HTMLDivElement>;
  hideActionButtons?: boolean;
  editorStore: EditorStoreLike;
  onAddComposition: (composition: CompositionTemplate) => void;
  onReplaceImage?: (newUrl: string) => void;
  parentCompositionElement?: ElementState;
};
export const isBasicTextElement = (activeElement: ElementState) => {
  const isBasicText =
    activeElement?.source?.type === 'text' &&
    activeElement.source.track !== KARAOKE_TRACK_NUMBER;
  return isBasicText;
};
/**
 * Returns the appropriate settings component for the given active element.
 * Returns only the inner control element without wrappers (MainPanel, ScrollableArea).
 */
export const getActiveElementSettings = ({
  activeElement,
  videoCreator,
  sidebarRef,
  editorStore,
  hideActionButtons = false,
  onAddComposition,
  onReplaceImage,
  parentCompositionElement,
}: GetActiveElementSettingsProps): React.ReactElement | null => {
  if (isBasicTextElement(activeElement)) {
    return (
      <TextSettings
        activeElement={activeElement}
        ref={sidebarRef}
        hideActionButtons={hideActionButtons}
        editorStore={editorStore}
      />
    );
  } else if (activeElement.source.type === 'shape') {
    return (
      <ShapeSettings
        activeElement={activeElement}
        ref={sidebarRef}
        hideActionButtons={hideActionButtons}
        editorStore={editorStore}
      />
    );
  } else if (activeElement.source.type === 'audio') {
    return <AudioSettings activeElement={activeElement} />;
  } else if (videoCreator.isImageElement(activeElement)) {
    return (
      <ImageSettings
        activeElement={activeElement}
        editorStore={editorStore}
        onReplaceImage={onReplaceImage}
        parentCompositionElement={parentCompositionElement}
      />
    );
  } else if (videoCreator.isOriginalVideoElement(activeElement.source)) {
    return null; // Not consistent with layouts and not well tested, so hiding sidebar for main video element for now
  } else if (videoCreator.isVideoElement(activeElement)) {
    return (
      <VideoSettings activeElement={activeElement} editorStore={editorStore} />
    );
  } else if (activeElement.source.type === 'composition') {
    // TODO: The current check for composition elements only verifies
    // source.type === 'composition'. Improve this check by validating the
    // relevant fields in extraElementData to ensure the element is a true
    // composition.
    return (
      <CompositionSettings
        activeElement={activeElement}
        onAddComposition={onAddComposition}
      />
    );
  }

  return null;
};
